import React, { useState, useEffect, useCallback } from 'react';
import {
  App,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Tag,
  Space,
  Card,
} from 'antd';
import {
  DeleteOutlined,
} from '@ant-design/icons';
import { PROTOCOL_STEP_ICONS } from './protocolIcons';
import { DetailDrawer } from '../../../../../components/shared';
import {
  ProtocolFlowNode,
  ProtocolFlowEdge,
  ProtocolFlowNodeData,
  ProtocolStep,
  Protocol,
  StepType,
  PROTOCOL_NODE_CONFIG,
  StepConfiguration,
  ConditionalNodeConfig,
  ALIQUOT_CONTAINER_TYPES,
  ALIQUOT_STORAGE_TEMPERATURES,
  ALIQUOT_LABEL_VARS,
} from '../../../../../types/lab/ProtocolTypes';

import StepMethodsEditor from './stepEditingComponents/StepMethodsEditor';
import StepMaterialsEditor from './stepEditingComponents/StepMaterialsEditor';
import StepFieldsEditor from './stepEditingComponents/StepFieldsEditor';
import StepEquipmentEditor from './stepEditingComponents/StepEquipmentEditor';
import StepResultsEditor from './stepEditingComponents/StepResultsEditor';
import ActionNodeEditor from './stepEditingComponents/ActionNodeEditor';
import ConditionalNodeEditor from './stepEditingComponents/ConditionalNodeEditor';

const { TextArea } = Input;

// ── Props ─────────────────────────────────────────────────────────────────────
interface ProtocolNodePropertiesDrawerProps {
  node: ProtocolFlowNode | null;
  open: boolean;
  onClose: () => void;
  onUpdate: (nodeId: string, data: Partial<ProtocolFlowNodeData>) => void;
  onDelete: (nodeId: string) => void;
  protocol: Partial<Protocol>;
  /**
   * Owning clinic id, needed by ActionNodeEditor's fetchActionTypeOptions().
   * A sibling of companyName rather than a field on `protocol` — labiq dropped
   * protocols.company_id (migration 2026_07_27_042746) and Protocol no longer carries it.
   */
  companyId?: number;
  companyName?: string;
  /** All canvas nodes — needed by ConditionalNodeEditor for upstream step lookup. */
  allNodes: ProtocolFlowNode[];
  /** All canvas edges — needed by ConditionalNodeEditor for routing preview. */
  allEdges: ProtocolFlowEdge[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
/** Tabs available for a given step type. */
function getTabsForType(type: StepType) {
  switch (type) {
    case 'start':       return ['details'];
    case 'end':         return ['details'];
    case 'conditional': return ['details', 'conditions'];
    case 'manual':      return ['details', 'methods', 'materials', 'fields', 'equipment'];
    case 'automated':   return ['details', 'actions'];
    case 'qc':          return ['details', 'fields', 'equipment'];
    case 'results':     return ['details', 'results'];
    case 'aliquot':     return ['details', 'aliquot'];
    default:            return ['details'];
  }
}

const TAB_LABELS: Record<string, string> = {
  details:    'Details',
  methods:    'Methods',
  materials:  'Materials',
  fields:     'Data Fields',
  equipment:  'Equipment',
  actions:    'Automation Actions',
  results:    'Results Config',
  aliquot:    'Aliquot Config',
  conditions: 'Conditions & Logic',
};

// ── Main component ────────────────────────────────────────────────────────────
const ProtocolNodePropertiesDrawer: React.FC<ProtocolNodePropertiesDrawerProps> = ({
  node,
  open,
  onClose,
  onUpdate,
  onDelete,
  protocol,
  companyId,
  companyName,
  allNodes,
  allEdges,
}) => {
  const { modal } = App.useApp();
  const [activeTab, setActiveTab] = useState('details');

  // Reset tab when a different node is selected
  useEffect(() => {
    if (node) {
      const tabs = getTabsForType(node.data.step_type);
      setActiveTab(tabs[0]);
    }
  }, [node?.id]);

  // ── Update helpers ────────────────────────────────────────────────────────
  const update = useCallback(
    (patch: Partial<ProtocolFlowNodeData>) => {
      if (!node) return;
      onUpdate(node.id, patch);
    },
    [node?.id, onUpdate],
  );

  const updateConfig = useCallback(
    (patch: Partial<StepConfiguration>) => {
      if (!node) return;
      onUpdate(node.id, {
        configuration: { ...(node.data.configuration ?? {}), ...patch },
      });
    },
    [node?.id, node?.data?.configuration, onUpdate],
  );

  const updateAliquotConfig = useCallback(
    (field: string, value: unknown) => {
      if (!node) return;
      const prev = (node.data.configuration as any)?.aliquot_configuration ?? {};
      updateConfig({ aliquot_configuration: { ...prev, [field]: value } } as any);
    },
    [node?.id, updateConfig],
  );

  // Adapter for sub-editors that mutate the full ProtocolStep object
  const handleStepChange = useCallback(
    (updated: Partial<ProtocolStep>) => {
      if (!node) return;
      onUpdate(node.id, updated as Partial<ProtocolFlowNodeData>);
    },
    [node?.id, onUpdate],
  );

  const handleDeleteConfirm = () => {
    if (!node) return;
    modal.confirm({
      title: 'Remove node?',
      content: 'This will also remove all connections to and from this node.',
      okText: 'Remove',
      okType: 'danger',
      onOk: () => {
        onDelete(node.id);
        onClose();
      },
    });
  };

  if (!node) return null;

  const cfg = PROTOCOL_NODE_CONFIG[node.data.step_type];
  const availableTabs = getTabsForType(node.data.step_type);
  const aliquotCfg: Record<string, any> = (node.data.configuration as any)?.aliquot_configuration ?? {};

  // ── Tab content renderers ─────────────────────────────────────────────────
  const renderDetailsTab = () => {
    const { step_type } = node.data;
    return (
      <Form layout="vertical" size="small" style={{ padding: '16px 0' }}>
        {/* Label — shared by all types */}
        <Form.Item label="Label" style={{ marginBottom: 12 }}>
          <Input
            value={node.data.label}
            onChange={(e) => update({ label: e.target.value })}
            placeholder={`${cfg.label} label…`}
          />
        </Form.Item>

        {/* Description — shared */}
        <Form.Item label="Description" style={{ marginBottom: 12 }}>
          <TextArea
            rows={2}
            value={node.data.description ?? ''}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Optional description or notes"
          />
        </Form.Item>

        {/* Duration — lab steps only */}
        {!['start', 'end', 'conditional'].includes(step_type) && (
          <Form.Item label="Estimated Duration (min)" style={{ marginBottom: 12 }}>
            <InputNumber
              min={1}
              max={1440}
              value={node.data.duration_estimate}
              onChange={(v) => update({ duration_estimate: v ?? 1 })}
              style={{ width: '100%' }}
              addonAfter="min"
            />
          </Form.Item>
        )}

        {/* End state — end node only */}
        {step_type === 'end' && (
          <Form.Item label="End State" style={{ marginBottom: 12 }}>
            <Select
              value={(node.data as any).end_state ?? 'success'}
              onChange={(v) => update({ end_state: v } as any)}
              options={[
                { value: 'success',  label: '✅  Success — protocol completed normally' },
                { value: 'failure',  label: '❌  Failure — protocol ended with errors' },
                { value: 'on_hold',  label: '⏸  On Hold — protocol paused at end' },
              ]}
            />
          </Form.Item>
        )}

        {/* Timeout — lab steps only */}
        {!['start', 'end', 'conditional'].includes(step_type) && (
          <Form.Item label="Step Timeout" style={{ marginBottom: 0 }}>
            <InputNumber
              min={1}
              max={10080}
              value={node.data.timeout_minutes}
              onChange={(v) => update({ timeout_minutes: v ?? undefined })}
              style={{ width: '100%' }}
              addonAfter="min"
              placeholder="No timeout"
            />
          </Form.Item>
        )}
      </Form>
    );
  };

  const renderMethodsTab = () => (
    <StepMethodsEditor
      methods={node.data.method ?? []}
      onMethodsChange={(methods) => update({ method: methods })}
    />
  );

  const renderMaterialsTab = () => (
    <StepMaterialsEditor
      materials={node.data.materials ?? []}
      onMaterialsChange={(materials) => update({ materials })}
    />
  );

  const renderFieldsTab = () => (
    <StepFieldsEditor
      fields={node.data.configuration?.required_fields ?? []}
      onFieldsChange={(fields) => updateConfig({ required_fields: fields })}
    />
  );

  const renderEquipmentTab = () => (
    <StepEquipmentEditor
      equipment={node.data.configuration?.equipment_requirements ?? []}
      onEquipmentChange={(equipment) => updateConfig({ equipment_requirements: equipment })}
    />
  );

  /** Actions tab — automated nodes only */
  const renderActionsTab = () => (
    <ActionNodeEditor
      step={node.data as unknown as Partial<ProtocolStep>}
      onStepChange={handleStepChange}
      companyId={companyId}
      companyName={companyName}
      actionsOnly
    />
  );

  const renderResultsTab = () => (
    <StepResultsEditor
      step={node.data as unknown as Partial<ProtocolStep>}
      onStepChange={handleStepChange}
      protocol={protocol}
    />
  );

  const renderAliquotTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Card size="small" title="Aliquot Settings">
        <Form layout="vertical" size="small">
          <Form.Item label="Aliquots per Test Type *" style={{ marginBottom: 10 }}>
            <InputNumber
              min={1}
              max={100}
              value={aliquotCfg.aliquot_count_per_test_type ?? 1}
              onChange={(v) => updateAliquotConfig('aliquot_count_per_test_type', v)}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="Volume per Aliquot (µL)" style={{ marginBottom: 10 }}>
            <InputNumber
              min={0}
              value={aliquotCfg.volume_per_aliquot}
              onChange={(v) => updateAliquotConfig('volume_per_aliquot', v)}
              style={{ width: '100%' }}
              addonAfter="µL"
              placeholder="Optional"
            />
          </Form.Item>
          <Form.Item label="Container Type" style={{ marginBottom: 10 }}>
            <Select
              value={aliquotCfg.container_type}
              onChange={(v) => updateAliquotConfig('container_type', v)}
              options={ALIQUOT_CONTAINER_TYPES}
              placeholder="Select container type"
            />
          </Form.Item>
          {aliquotCfg.container_type === 'custom' && (
            <Form.Item label="Custom Container Description" style={{ marginBottom: 0 }}>
              <Input
                value={aliquotCfg.custom_container_description ?? ''}
                onChange={(e) => updateAliquotConfig('custom_container_description', e.target.value)}
                placeholder="Describe the custom container"
              />
            </Form.Item>
          )}
        </Form>
      </Card>

      <Card size="small" title="Labelling & Storage">
        <Form layout="vertical" size="small">
          <Form.Item label="Label Prefix" style={{ marginBottom: 10 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={aliquotCfg.label_prefix ?? ''}
                onChange={(e) => updateAliquotConfig('label_prefix', e.target.value)}
                placeholder="e.g. ALQ-"
              />
              <Select
                placeholder="Insert variable"
                style={{ width: 180 }}
                value={undefined}
                options={ALIQUOT_LABEL_VARS}
                onChange={(v) => updateAliquotConfig('label_prefix', `${aliquotCfg.label_prefix ?? ''}${v}`)}
              />
            </Space.Compact>
          </Form.Item>
          <Form.Item label="Label Suffix" style={{ marginBottom: 10 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={aliquotCfg.label_suffix ?? ''}
                onChange={(e) => updateAliquotConfig('label_suffix', e.target.value)}
                placeholder="e.g. -{{date}}"
              />
              <Select
                placeholder="Insert variable"
                style={{ width: 180 }}
                value={undefined}
                options={ALIQUOT_LABEL_VARS}
                onChange={(v) => updateAliquotConfig('label_suffix', `${aliquotCfg.label_suffix ?? ''}${v}`)}
              />
            </Space.Compact>
          </Form.Item>
          <div
            style={{
              background: 'var(--mg-bg-subtle)',
              border: '1px solid var(--mg-border)',
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 11,
              color: 'var(--mg-text-primary)',
              marginBottom: 10,
            }}
          >
            <strong>Template variables:</strong>{' '}
            {ALIQUOT_LABEL_VARS.map((v) => (
              <Tag key={v.value} style={{ fontSize: 10 }}>{v.value}</Tag>
            ))}
          </div>
          <Form.Item label="Storage Temperature" style={{ marginBottom: 10 }}>
            <Select
              value={aliquotCfg.storage_temperature}
              onChange={(v) => updateAliquotConfig('storage_temperature', v)}
              options={ALIQUOT_STORAGE_TEMPERATURES}
              placeholder="Select temperature"
            />
          </Form.Item>
          <Form.Item label="Special Instructions" style={{ marginBottom: 0 }}>
            <TextArea
              rows={2}
              value={aliquotCfg.special_instructions ?? ''}
              onChange={(e) => updateAliquotConfig('special_instructions', e.target.value)}
              placeholder="Any special handling instructions"
            />
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" title="Quality Control">
        <Form layout="vertical" size="small">
          <Form.Item label="Require Volume Verification" style={{ marginBottom: 10 }}>
            <Switch
              checked={aliquotCfg.require_volume_check ?? false}
              onChange={(v) => updateAliquotConfig('require_volume_check', v)}
            />
          </Form.Item>
          {aliquotCfg.require_volume_check && (
            <Form.Item label="Volume Tolerance (%)" style={{ marginBottom: 10 }}>
              <InputNumber
                min={0}
                max={100}
                value={aliquotCfg.volume_tolerance}
                onChange={(v) => updateAliquotConfig('volume_tolerance', v)}
                style={{ width: '100%' }}
                addonAfter="%"
              />
            </Form.Item>
          )}
          <Form.Item label="Require Barcode Scan" style={{ marginBottom: 0 }}>
            <Switch
              checked={aliquotCfg.require_barcode_scan ?? false}
              onChange={(v) => updateAliquotConfig('require_barcode_scan', v)}
            />
          </Form.Item>
        </Form>
      </Card>
    </div>
  );

  const renderConditionsTab = () => (
    <ConditionalNodeEditor
      nodeId={node.id}
      allNodes={allNodes}
      allEdges={allEdges}
      config={node.data.conditional_config as ConditionalNodeConfig | undefined}
      onChange={(cfg) => update({ conditional_config: cfg })}
    />
  );

  // ── Tab content router ────────────────────────────────────────────────────
  const renderTabContent = (tabKey: string) => {
    switch (tabKey) {
      case 'details':    return renderDetailsTab();
      case 'methods':    return renderMethodsTab();
      case 'materials':  return renderMaterialsTab();
      case 'fields':     return renderFieldsTab();
      case 'equipment':  return renderEquipmentTab();
      case 'actions':    return renderActionsTab();
      case 'results':    return renderResultsTab();
      case 'aliquot':    return renderAliquotTab();
      case 'conditions': return renderConditionsTab();
      default:           return null;
    }
  };

  // ── Build DetailDrawer tabs ───────────────────────────────────────────────
  const tabItems = availableTabs.map((key) => ({
    key,
    label: TAB_LABELS[key] ?? key,
    children: (
      <div style={{ padding: '0 24px 24px' }}>
        {renderTabContent(key)}
      </div>
    ),
  }));

  // ── Avatar (coloured icon) ────────────────────────────────────────────────
  const avatar = (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: cfg.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 18,
        flexShrink: 0,
      }}
    >
      {PROTOCOL_STEP_ICONS[node.data.step_type]}
    </div>
  );

  const titleMeta = node.data.step_number != null ? (
    <Tag
      style={{
        marginLeft: 8,
        borderColor: cfg.color,
        color: cfg.color,
        background: `${cfg.color}12`,
        fontWeight: 700,
      }}
    >
      Step {node.data.step_number}
    </Tag>
  ) : undefined;

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      width="72vw"
      avatar={avatar}
      title={
        <span style={{ fontWeight: 700 }}>
          {node.data.label || cfg.label}
        </span>
      }
      subtitle={cfg.label}
      titleMeta={titleMeta}
      tabs={tabItems}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      footer={
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={handleDeleteConfirm}
        >
          Remove Node
        </Button>
      }
    />
  );
};

export default ProtocolNodePropertiesDrawer;
